import React, { useState, useEffect, useRef } from 'react';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import axios from 'axios';
import UnstructuredThreadDetails from './UnstructuredThreadDetails';
import UnstructuredThreadForm from './UnstructuredThreadForm';
import UnstructuredThreadList from './UnstructuredThreadList';
import ReferenceLibrary from './ReferenceLibrary';
import GetCookie from '../../utils/GetCookie'
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  setThreads,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setSlrProjects,
  setSelectedSlrProjects,
  setShowForm,
  setShowRefLibrary,
  setFormData,
  setSelectedUsers,
  setThreadDetails,
  loadEditingThread,
  openThreadDetails,
  resetForm,
} from '../../../store/unstructuredThreadSlice';

const UnstructuredThreadManagement = () => {
  const dispatch = useDispatch();
  const locationStateApplied = useRef(false);
  
  const {
    threads, total, page, size, search, filterType,
    slrProjects, selectedSlrProjects,
    showForm, showRefLibrary, editingThread, threadDetails, formData, selectedUsers,
  } = useSelector((state) => state.unstructuredThread);
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refExtractFiles, setRefExtractFiles] = useState(new Set());
  const [refLibraryTotal, setRefLibraryTotal] = useState(0);
  // Files state
  const [files, setFiles] = useState({
    pdfFiles: [],
    wordFiles: [],
    pptFiles: [],
    textFiles: [],
    excelFiles: [],
    csvFiles: []
  });

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  // API base URL
  const API_BASE_URL = '/pceapi/threads/unstructured';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  useEffect(() => {
    if (
      !locationStateApplied.current &&
      location.state?.fromSLR &&
      location.state?.slrProjectId
    ) {
      locationStateApplied.current = true;
      dispatch(setSelectedSlrProjects([location.state.slrProjectId]));
      dispatch(setPage(1));
      return; 
    }

    fetchThreads();
  }, [page, size, search, refreshTrigger, filterType, selectedSlrProjects]);

  useEffect(() => {
    if (threadDetails?.thread?.id) {
      fetchThreadDetails(threadDetails.thread.id);
    }
  }, []);

  // Fetch threads with pagination and search
  const fetchThreads = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading Data Collections');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`, 
        {
          page,
          size,
          search: search || undefined,
          filter_type: filterType,
          slr_project_ids: selectedSlrProjects.length > 0 ? selectedSlrProjects : undefined,
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load Data Collections');
        setIsErrorPopupVisible(true);
        return;
      }
      
      dispatch(setThreads(data.data || []));
      dispatch(setTotal(data.total || 0));
      dispatch(setSlrProjects(data.slr_projects || []));
    } catch (error) {
      setPopupErrorMessage(error.message || 'Failed to load Data Collections');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };


  // Fetch thread details for viewing
  const fetchThreadDetails = async (threadId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading Data Collection details');
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/details/${threadId}`,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load Data Collection details');
        setIsErrorPopupVisible(true);
        return;
      }
      
      dispatch(setThreadDetails(data.data));
    } catch (error) {
      console.error('Error fetching Data Collection details:', error);
      setPopupErrorMessage(error.message || 'Failed to load Data Collection details');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const resetLocalForm = () => {
  dispatch(resetForm());  
  setFiles({              
    pdfFiles: [], wordFiles: [], pptFiles: [],
    textFiles: [], excelFiles: [], csvFiles: [],
  });
  setRefExtractFiles(new Set());  // Add this line
};

  // Form submission handler
  const handleSubmit = async (e, refExtractFiles = new Set()) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setPopupErrorMessage('Data Collection name is required');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingThread ? 'Updating Data Collection' : 'Creating Data Collection');
    
    try {
      // Create FormData object for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('thread_access_level', formData.thread_access_level);
      
      // Append tags
      if (formData.tags.length > 0) {
        formDataToSend.append('tags', JSON.stringify(formData.tags));
      }
      
      // Append selected users for private threads
      if (formData.thread_access_level === 'PRIVATE' && selectedUsers.length > 0) {
        formDataToSend.append('user_access', JSON.stringify(selectedUsers));
      }
      const fileCategories = ['pdfFiles', 'wordFiles', 'pptFiles', 'textFiles', 'excelFiles', 'csvFiles'];

fileCategories.forEach(category => {
    const fileList = files[category];
    if (fileList && fileList.length > 0) {
        const metadataList = fileList.map((item) => ({
            file_id: item.id,
            file_name: item.file.name,
            file_size_mb: (item.file.size / (1024 * 1024)).toFixed(2),
            extract_references: refExtractFiles.has(item.id)
        }));
        formDataToSend.append(`${category}_metadata`, JSON.stringify(metadataList));
    }
});
         
      // Append files if any
      Object.entries(files).forEach(([key, fileList]) => {
    if (fileList.length > 0) {
        for (let i = 0; i < fileList.length; i++) {
            formDataToSend.append(key, fileList[i].file);
        }
    }
});
      
      let response;
      
      if (editingThread) {
        // Update existing thread
        formDataToSend.append('thread_id', editingThread.id);
        response = await axios.post(
          `${API_BASE_URL}/update`,
          formDataToSend,
          { 
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Create new thread
        response = await axios.post(
          `${API_BASE_URL}/create`,
          formDataToSend,
          { 
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || (editingThread ? 'Failed to update Data Collection' : 'Failed to create Data Collection'));
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || (editingThread ? 'Data Collection updated successfully' : 'Data Collection created successfully'));
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
      resetLocalForm();
    } catch (error) {
      console.error('Submit error:', error);
      setPopupErrorMessage(error.message || (editingThread ? 'Failed to update Data Collection' : 'Failed to create Data Collection'));
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Open form for editing a thread
  const handleOpenEditForm = (thread) => {
    dispatch(loadEditingThread(thread));
    setFiles({ pdfFiles: [], wordFiles: [], pptFiles: [], textFiles: [], excelFiles: [], csvFiles: [] });
  };

  // Open thread details view
  const handleOpenThreadDetails = (threadId) => {
    dispatch(openThreadDetails());
    fetchThreadDetails(threadId);
  };

  // Close thread details view
  const handleCloseThreadDetails = () => {
    dispatch(setThreadDetails(null));
  };

  // Popup close handlers
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };

  // Add this function to UnstructuredThreadManagement
const handleRefreshThreadDetails = () => {
  if (threadDetails && threadDetails.thread) {
    fetchThreadDetails(threadDetails.thread.id);
  }
};

  const handleSetFormData = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setFormData(updater(formData)));
    } else {
      dispatch(setFormData(updater));
    }
  };

  const handleSetSelectedUsers = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setSelectedUsers(updater(selectedUsers)));
    } else {
      dispatch(setSelectedUsers(updater));
    }
  };

  const handleSetPage = (val) => dispatch(setPage(val));
  const handleSetSize = (val) => dispatch(setSize(val));
  const handleSetSearch = (val) => dispatch(setSearch(val));
  const handleSetFilterType = (val) => dispatch(setFilterType(val));
  const handleSetSelectedSlrProjects = (val) => {
    dispatch(setSelectedSlrProjects(val));
    dispatch(setPage(1));
  };

  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">
                  {showRefLibrary ? (
                     <>Reference Library <span className="ev-light-reg ev-bold-med">({refLibraryTotal})</span></>
                  ) : (
                    <>Unstructured Data Collections <span className="ev-light-reg">({total})</span></>
                  )}
                </p>
                {showRefLibrary ? (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-med ev-bold-reg"
                    onClick={() => dispatch(setShowRefLibrary(false))}
                  >
                  Back to Data Collections
                  </button>
                ) : !showForm && !threadDetails && (
                  <div className="d-flex gap-2">
                    <button
                      className="ev-btn-blue-outline-round ev-btn-med ev-bold-reg"
                      onClick={() => dispatch(setShowRefLibrary(true))}
                    >
                      <i className="bi bi-book me-1"></i> Reference Library
                    </button>
                    <button 
                      className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg" 
                      onClick={() => {
                        resetLocalForm();
                        dispatch(setShowForm(true));
                      }}
                    >
                      <i className="bi bi-plus-circle me-1"></i> Create Data Collection
                    </button>
                    {location.state?.fromSLR && (
                      <button
                        className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg"
                        onClick={() => navigate(-1)}
                      >
                        <i className="bi bi-arrow-left me-1"></i> Back to Projects
                      </button>
                    )}
                  </div>
                )}
              </div>
              <p className="ev-message-card2 mb-4">
                {showRefLibrary 
                  ? 'Browse, edit, and deduplicate references extracted from your documents.'
                  : 'Manage unstructured data collections and document processing RAG pipelines.'}
              </p>
              
              {/* Show form, thread details, or thread list based on state */}
              {showRefLibrary ? (
                <ReferenceLibrary
                  onClose={() => dispatch(setShowRefLibrary(false))}
                  onCountChange={setRefLibraryTotal}
                />
              ) : showForm ? (
                <UnstructuredThreadForm
                  editingThread={editingThread}
                  formData={formData}
                  setFormData={handleSetFormData}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={handleSetSelectedUsers}
                  files={files}
                  setFiles={setFiles}
                  refExtractFiles={refExtractFiles}        // Add this line
                  setRefExtractFiles={setRefExtractFiles}  // Add this line
                  onSubmit={handleSubmit}
                  onCancel={() => resetLocalForm()}
                />
              ) : threadDetails ? (
                <UnstructuredThreadDetails 
                threadDetails={threadDetails} 
                onClose={handleCloseThreadDetails}
                onRefresh={handleRefreshThreadDetails}
                />
              ) : (
                <UnstructuredThreadList
                  threads={threads}
                  total={total}
                  page={page}
                  setPage={handleSetPage}
                  size={size}
                  setSize={handleSetSize} 
                  search={search}
                  setSearch={handleSetSearch}
                  filterType={filterType}
                  setFilterType={handleSetFilterType}
                  slrProjects={slrProjects}
                  selectedSlrProjects={selectedSlrProjects}
                  setSelectedSlrProjects={handleSetSelectedSlrProjects}
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  onEditThread={handleOpenEditForm}
                  onViewThreadDetails={handleOpenThreadDetails}
                />
              )}
            </div>
          </main>
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default UnstructuredThreadManagement;